/**
 * Best-effort translation of GeoEvent regular-expression MATCHES patterns into
 * Arcade boolean expressions.
 *
 * Velocity's Arcade profile has NO native regular-expression support: there is
 * no match/test/regex function, `Find` performs literal search with no
 * wildcards, and `Replace`/`Split` operate on literal text only. Arbitrary
 * regular expressions therefore cannot be translated. This module recognizes a
 * bounded set of common, structured pattern shapes and emits an Arcade
 * approximation built from text functions (Count, Mid, ToCharCode, Left, Right,
 * Find). Unrecognized patterns return null so the caller can emit an honest
 * "no native regex" note with real alternatives instead of a misleading
 * "rewrite as Arcade" instruction.
 *
 * GeoEvent's MATCHES operator uses Java `String.matches()` semantics — the
 * entire value must match — so translations are anchored/full-match.
 */

export interface RegexMatchTranslation {
  /** Arcade boolean expression fragment (no surrounding comment). */
  expression: string;
  /** Short human-readable description of the matched shape, for notes. */
  summary: string;
}

type CharClass = 'digit' | 'alpha' | 'lower' | 'upper' | 'alnum';

type Atom =
  | { kind: 'class'; cls: CharClass; count: number }
  | { kind: 'lit'; text: string }
  | { kind: 'optlit'; ch: string };

/** Maximum unrolled match length before a pattern is considered too large. */
const MAX_MATCH_LENGTH = 24;

/** Characters that are safe to treat as literal text (no regex meaning). */
const SAFE_LITERAL = /^[A-Za-z0-9 _-]*$/;

/** Builds an Arcade predicate that tests whether a single-character expression belongs to a class. */
function classPredicate(cls: CharClass, charExpr: string): string {
  switch (cls) {
    case 'digit':
      return `ToCharCode(${charExpr}) >= 48 && ToCharCode(${charExpr}) <= 57`;
    case 'upper':
      return `ToCharCode(${charExpr}) >= 65 && ToCharCode(${charExpr}) <= 90`;
    case 'lower':
      return `ToCharCode(${charExpr}) >= 97 && ToCharCode(${charExpr}) <= 122`;
    case 'alpha':
      return `ToCharCode(Upper(${charExpr})) >= 65 && ToCharCode(Upper(${charExpr})) <= 90`;
    case 'alnum':
      return (
        `(ToCharCode(Upper(${charExpr})) >= 65 && ToCharCode(Upper(${charExpr})) <= 90)` +
        ` || (ToCharCode(${charExpr}) >= 48 && ToCharCode(${charExpr}) <= 57)`
      );
  }
}

/** Maps a character-class body (bracket inner or shorthand) to a CharClass, or null. */
function resolveCharClass(body: string): CharClass | null {
  const normalized = body.replace(/\s+/g, '');
  switch (normalized) {
    case 'A-Za-z':
    case 'a-zA-Z':
      return 'alpha';
    case 'a-z':
      return 'lower';
    case 'A-Z':
      return 'upper';
    case '0-9':
      return 'digit';
    case 'A-Za-z0-9':
    case '0-9A-Za-z':
    case 'a-zA-Z0-9':
      return 'alnum';
    default:
      return null;
  }
}

/** Splits a pattern on top-level `|` (not inside [] or ()). */
function splitTopLevelAlternation(pattern: string): string[] {
  const parts: string[] = [];
  let depthBracket = 0;
  let depthParen = 0;
  let current = '';
  for (let i = 0; i < pattern.length; i++) {
    const ch = pattern[i];
    if (ch === '\\') {
      current += ch + (pattern[i + 1] ?? '');
      i++;
      continue;
    }
    if (ch === '[') depthBracket++;
    else if (ch === ']' && depthBracket > 0) depthBracket--;
    else if (ch === '(') depthParen++;
    else if (ch === ')' && depthParen > 0) depthParen--;
    if (ch === '|' && depthBracket === 0 && depthParen === 0) {
      parts.push(current);
      current = '';
      continue;
    }
    current += ch;
  }
  parts.push(current);
  return parts;
}

/** Returns the literal text of a pattern segment if it contains no regex metacharacters, else null. */
function asPureLiteral(pattern: string): string | null {
  // Support simple escaped literals like \. \- by unescaping them.
  const unescaped = pattern.replace(/\\([.\-\/])/g, '$1');
  return SAFE_LITERAL.test(unescaped) ? unescaped : null;
}

/** Parses a wildcard-free pattern into a bounded atom list, or null when unsupported. */
function parseAtoms(pattern: string): Atom[] | null {
  const atoms: Atom[] = [];
  let i = 0;
  const readCount = (): number | null => {
    if (pattern[i] !== '{') return 1;
    const close = pattern.indexOf('}', i);
    if (close === -1) return null;
    const body = pattern.slice(i + 1, close);
    if (!/^\d+$/.test(body)) return null; // {n,m}, {n,} unsupported in v1
    i = close + 1;
    return Number(body);
  };
  while (i < pattern.length) {
    const ch = pattern[i];
    if (ch === '[') {
      const close = pattern.indexOf(']', i);
      if (close === -1) return null;
      const cls = resolveCharClass(pattern.slice(i + 1, close));
      if (!cls) return null;
      i = close + 1;
      const count = readCount();
      if (count === null || pattern[i] === '+' || pattern[i] === '*' || pattern[i] === '?') return null;
      atoms.push({ kind: 'class', cls, count });
      continue;
    }
    if (ch === '\\') {
      const next = pattern[i + 1];
      let cls: CharClass | null = null;
      if (next === 'd') cls = 'digit';
      else if (next === 'w') cls = 'alnum';
      if (!cls) return null;
      i += 2;
      const count = readCount();
      if (count === null || pattern[i] === '+' || pattern[i] === '*' || pattern[i] === '?') return null;
      atoms.push({ kind: 'class', cls, count });
      continue;
    }
    // Regex metacharacters not handled here mean the pattern is unsupported.
    if ('.*+(){}|^$?'.includes(ch as string)) {
      // A literal immediately followed by '?' is an optional literal.
      return null;
    }
    if (pattern[i + 1] === '?') {
      atoms.push({ kind: 'optlit', ch: ch as string });
      i += 2;
      continue;
    }
    // Accumulate a run of plain literal characters.
    let text = '';
    while (i < pattern.length && !'.*+(){}|^$[]\\'.includes(pattern[i] as string) && pattern[i + 1] !== '?') {
      text += pattern[i];
      i++;
    }
    if (text.length === 0) return null;
    atoms.push({ kind: 'lit', text });
  }
  return atoms;
}

function describeAtoms(atoms: Atom[]): string {
  const classNames: Record<CharClass, string> = {
    digit: 'digit',
    alpha: 'letter',
    lower: 'lowercase letter',
    upper: 'uppercase letter',
    alnum: 'letter/digit',
  };
  return atoms
    .map((a) =>
      a.kind === 'class'
        ? `${a.count} ${classNames[a.cls]}${a.count > 1 ? 's' : ''}`
        : a.kind === 'lit'
          ? `"${a.text}"`
          : `optional "${a.ch}"`,
    )
    .join(' + ');
}

/**
 * Attempts to translate a GeoEvent regular-expression MATCHES pattern into an
 * Arcade boolean expression evaluated against `fieldRef`. Returns null when the
 * pattern falls outside the supported bounded grammar.
 */
export function translateRegexMatchToArcade(
  fieldRef: string,
  patternRaw: string,
): RegexMatchTranslation | null {
  if (typeof patternRaw !== 'string' || patternRaw.length === 0) return null;

  // Top-level alternation: translate each alternative and OR the results.
  const alternatives = splitTopLevelAlternation(patternRaw);
  if (alternatives.length > 1) {
    const translated = alternatives.map((alt) => translateRegexMatchToArcade(fieldRef, alt));
    if (translated.some((t) => t === null)) return null;
    return {
      expression: translated.map((t) => `(${t!.expression})`).join(' || '),
      summary: `one of: ${translated.map((t) => t!.summary).join(', ')}`,
    };
  }

  let pattern = patternRaw;
  if (pattern.startsWith('^')) pattern = pattern.slice(1);
  if (/(?<!\\)\$$/.test(pattern)) pattern = pattern.slice(0, -1);

  // Leading / trailing `.*` or `.+` wildcards around a pure literal core.
  const leadWild = /^\.[*+]/.test(pattern);
  if (leadWild) pattern = pattern.replace(/^\.[*+]/, '');
  const trailWild = /\.[*+]$/.test(pattern);
  if (trailWild) pattern = pattern.replace(/\.[*+]$/, '');

  if (leadWild || trailWild) {
    const literal = asPureLiteral(pattern);
    if (literal === null || literal.length === 0) return null;
    const quoted = JSON.stringify(literal);
    if (leadWild && trailWild) {
      return { expression: `Find(${quoted}, ${fieldRef}) != -1`, summary: `contains ${quoted}` };
    }
    if (trailWild) {
      return { expression: `Left(${fieldRef}, ${literal.length}) == ${quoted}`, summary: `starts with ${quoted}` };
    }
    return { expression: `Right(${fieldRef}, ${literal.length}) == ${quoted}`, summary: `ends with ${quoted}` };
  }

  // No wildcards: full-match against a bounded atom sequence.
  const atoms = parseAtoms(pattern);
  if (!atoms || atoms.length === 0) return null;

  if (atoms.every((a) => a.kind === 'lit')) {
    const literal = atoms.map((a) => (a as { text: string }).text).join('');
    return { expression: `${fieldRef} == ${JSON.stringify(literal)}`, summary: `equals ${JSON.stringify(literal)}` };
  }

  // Strip optional literals from the value (approximation) and validate the
  // remaining required sequence position by position.
  const optionalChars = [...new Set(atoms.filter((a) => a.kind === 'optlit').map((a) => (a as { ch: string }).ch))];
  let norm = fieldRef;
  for (const ch of optionalChars) {
    norm = `Replace(${norm}, ${JSON.stringify(ch)}, "")`;
  }
  const required = atoms.filter((a) => a.kind !== 'optlit');

  let position = 0;
  let totalLength = 0;
  const checks: string[] = [];
  for (const atom of required) {
    if (atom.kind === 'class') {
      for (let i = 0; i < atom.count; i++) {
        checks.push(`(${classPredicate(atom.cls, `Mid(${norm}, ${position}, 1)`)})`);
        position++;
      }
      totalLength += atom.count;
    } else if (atom.kind === 'lit') {
      checks.push(`Mid(${norm}, ${position}, ${atom.text.length}) == ${JSON.stringify(atom.text)}`);
      position += atom.text.length;
      totalLength += atom.text.length;
    }
  }
  if (totalLength === 0 || totalLength > MAX_MATCH_LENGTH) return null;

  const expression = [`Count(${norm}) == ${totalLength}`, ...checks].join(' && ');
  return { expression, summary: describeAtoms(required) };
}
